import React, { useState } from 'react';
import { generateUniqueDynamicBlock } from '../data/questionBank';
import { saveActivityToCloud } from '../firebase';
import InteractiveText from './InteractiveText';
import { getSpanishMeaning } from '../utils/dictionaryHelper';

/**
 * Limpia cualquier prefijo tipo 'A) ', 'B) ' y espacios duplicados en las opciones
 */
const cleanOptionText = (rawOpt) => {
  if (!rawOpt) return "";
  return String(rawOpt).replace(/^[A-D][)\.]\s*/i, "").replace(/\s+/g, " ").trim();
};

export default function MissionPlayer({ grade, currentUser }) {
  const [selectedUnit, setSelectedUnit] = useState(1);
  const [activeMode, setActiveTabMode] = useState('taller');

  const [questions, setQuestions] = useState(() => generateUniqueDynamicBlock(grade, 1, 20));

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOpt, setSelectedOpt] = useState('');
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [userAnswerHistory, setUserAnswerHistory] = useState([]);

  // Estado para la emergente de significado de palabras
  const [activeWordData, setActiveWordData] = useState(null);
  const [loadingWord, setLoadingWord] = useState(false);

  // Si el usuario no ha iniciado sesión, bloquear el acceso a los ejercicios
  if (!currentUser) {
    return (
      <div className="bg-slate-900/90 border border-slate-800 p-8 rounded-3xl text-center space-y-4 max-w-lg mx-auto shadow-2xl animate-fadeIn">
        <div className="text-4xl">🔒</div>
        <h3 className="text-xl font-bold text-white">Inicio de Sesión Requerido</h3>
        <p className="text-xs text-slate-300 leading-relaxed">
          Para realizar las misiones y ejercicios del Grado {grade}° y registrar tus calificaciones y puntos XP en la plataforma de la IE Enrique Olaya Herrera, debes iniciar sesión previamente.
        </p>
      </div>
    );
  }

  const handleSelectUnit = (unitNumber) => {
    setSelectedUnit(unitNumber);
    resetQuiz(unitNumber, activeMode);
  };

  const handleSelectMode = (mode) => {
    setActiveTabMode(mode);
    resetQuiz(selectedUnit, mode);
  };

  const resetQuiz = (unitNum, mode) => {
    const count = mode === 'icfes' ? 10 : 20;
    const freshQuestions = generateUniqueDynamicBlock(grade, unitNum, count);

    setQuestions(freshQuestions);
    setCurrentIndex(0);
    setSelectedOpt('');
    setScore(0);
    setCompleted(false);
    setFeedback(null);
    setUserAnswerHistory([]);
  };

  const handleReShuffleAll = () => {
    resetQuiz(selectedUnit, activeMode);
  };

  // Manejar clic en palabras para ver traducción
  const handleWordClick = async (cleanWord, fullToken) => {
    setLoadingWord(true);
    const data = await getSpanishMeaning(cleanWord);
    setActiveWordData(data);
    setLoadingWord(false);
  };

  // Reproducir pronunciación de audio
  const speakText = (text) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.85;
    window.speechSynthesis.speak(utterance);
  };

  const handleAnswerSubmit = () => {
    if (!selectedOpt) return;

    const currentQ = questions[currentIndex];
    const rawCorrect = currentQ.ans || currentQ.answer || "";
    const correctClean = cleanOptionText(rawCorrect);
    const userClean = cleanOptionText(selectedOpt);
    const expText = currentQ.exp || currentQ.explanation || 'Revisa la regla gramatical o vocabulario en la guía.';

    const isCorrect = userClean === correctClean;

    let explanationText = "";
    if (isCorrect) {
      explanationText = `¡Excelente trabajo! 🎯 Es la respuesta correcta.\n\n💡 Explicación: ${expText}`;
      setScore(prev => prev + 1);
      setFeedback({ type: 'success', text: explanationText });
    } else {
      explanationText = `Ánimo, la opción correcta era: "${correctClean}".\n\n💡 Explicación: ${expText}`;
      setFeedback({ type: 'error', text: explanationText });
    }

    setUserAnswerHistory(prev => [
      ...prev,
      {
        qNum: currentIndex + 1,
        question: currentQ.q,
        userAns: selectedOpt,
        correctAns: currentQ.answer || currentQ.ans,
        isCorrect,
        explanation: currentQ.explanation || currentQ.exp
      }
    ]);

    setTimeout(() => {
      setFeedback(null);
      setSelectedOpt('');
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setCompleted(true);
        saveActivityScore();
      }
    }, 2800);
  };

  const saveActivityScore = () => {
    const totalXP = score * 10;
    const pct = Math.round((score / questions.length) * 100);
    const actType = activeMode === 'icfes' ? 'Práctica ICFES (10 Preguntas)' : 'Taller Combinatorio (20 Preguntas)';

    const activityRecord = {
      id: currentUser ? currentUser.code : 'EOH-601-1234',
      name: currentUser ? currentUser.username : 'Estudiante EOH',
      group: currentUser && currentUser.grade ? ('Grupo ' + currentUser.grade) : ('Grupo ' + grade + '01'),
      topic: 'Unidad ' + selectedUnit + ' (Grado ' + grade + '°)',
      type: actType,
      score: score + ' / ' + questions.length,
      pct: pct,
      xp: totalXP,
      date: new Date().toLocaleDateString('es-CO')
    };

    saveActivityToCloud(activityRecord);

    const existingActivities = JSON.parse(localStorage.getItem('motor_pedagogico_activities') || '[]');
    existingActivities.unshift(activityRecord);
    localStorage.setItem('motor_pedagogico_activities', JSON.stringify(existingActivities));
  };

  return (
    <div className="space-y-6 text-slate-100 relative">
      
      {/* 1. SELECCIÓN DE UNIDAD (U1 a U5) */}
      <div className="flex flex-wrap gap-2 border-b border-slate-700/80 pb-3">
        {[1, 2, 3, 4, 5].map(uNum => (
          <button
            key={uNum}
            onClick={() => handleSelectUnit(uNum)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
              selectedUnit === uNum 
                ? 'bg-blue-600 text-white shadow-lg border border-blue-400' 
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            📘 Unidad {uNum}
          </button>
        ))}
      </div>

      {/* 2. NAVEGACIÓN DE BLOQUES: TALLER (20) O PRÁCTICA ICFES (10) */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => handleSelectMode('taller')}
          className={`flex-1 py-3 rounded-2xl font-bold text-xs transition cursor-pointer flex items-center justify-center gap-2 border ${
            activeMode === 'taller' 
              ? 'bg-indigo-600 border-indigo-400 text-white shadow-lg' 
              : 'bg-slate-800/80 border-slate-700 text-slate-300 hover:bg-slate-700'
          }`}
        >
          ✏️ Bloque 1: Taller Combinatorio (20 Preguntas Únicas)
        </button>

        <button
          onClick={() => handleSelectMode('icfes')}
          className={`flex-1 py-3 rounded-2xl font-bold text-xs transition cursor-pointer flex items-center justify-center gap-2 border ${
            activeMode === 'icfes' 
              ? 'bg-purple-600 border-purple-400 text-white shadow-lg' 
              : 'bg-slate-800/80 border-slate-700 text-slate-300 hover:bg-slate-700'
          }`}
        >
          🎯 Bloque 2: Práctica ICFES (10 Preguntas Únicas)
        </button>
      </div>

      {/* BANNER INFORMATIVO DE DICCIONARIO INTERACTIVO CON CLIC */}
      <div className="bg-blue-950/60 border border-blue-500/30 px-4 py-2.5 rounded-2xl text-xs text-blue-200 flex items-center justify-between gap-2 shadow-sm">
        <span>💡 <strong>Diccionario Interactivo EOH:</strong> Haz clic sobre cualquier palabra en inglés para ver su significado en español al instante.</span>
      </div>

      {/* 3. PREGUNTAS EN DESARROLLO */}
      {!completed ? (
        <div className="bg-slate-900/90 border border-slate-700 p-6 rounded-3xl space-y-6 shadow-2xl">
          
          {/* BARRA DE PROGRESO Y TIPOLOGÍA ICFES */}
          <div className="space-y-2">
            <div className="flex flex-wrap justify-between items-center text-xs font-bold text-slate-300 gap-2">
              <span className="flex items-center gap-2">
                <span className="bg-blue-600/30 text-blue-300 px-2.5 py-1 rounded-lg text-[11px] border border-blue-400/40">
                  {questions[currentIndex]?.type || 'ICFES'}
                </span>
                Pregunta {currentIndex + 1} de {questions.length}
              </span>
              <span className="text-blue-400">Progreso: {Math.round(((currentIndex + 1) / questions.length) * 100)}%</span>
            </div>
            
            <div className="w-full bg-slate-950 h-2.5 rounded-full overflow-hidden border border-slate-800">
              <div 
                className={`h-full transition-all duration-300 ${activeMode === 'icfes' ? 'bg-purple-500' : 'bg-blue-600'}`}
                style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* PREGUNTA ACTUAL CON PALABRAS INTERACTIVAS */}
          <div className="space-y-4">
            <h3 className="text-base md:text-lg font-extrabold text-white leading-relaxed whitespace-pre-line">
              <InteractiveText 
                text={questions[currentIndex]?.q} 
                onWordClick={handleWordClick} 
              />
            </h3>

            {/* OPCIONES ORDENADAS ALFABÉTICAMENTE A), B), C), D) SIN ESPACIOS DUPLICADOS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {questions[currentIndex]?.opts.map((optRaw, idx) => {
                const cleanText = cleanOptionText(optRaw);
                const letter = String.fromCharCode(65 + idx); // 'A', 'B', 'C', 'D'
                const fullOptionLabel = `${letter}) ${cleanText}`;
                const isSelected = cleanOptionText(selectedOpt) === cleanText;

                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedOpt(fullOptionLabel)}
                    className={`p-3.5 rounded-2xl text-xs font-bold text-left transition cursor-pointer border flex items-center gap-2 ${
                      isSelected 
                        ? 'bg-blue-600 border-blue-400 text-white shadow-lg scale-[1.02]' 
                        : 'bg-slate-950/80 border-slate-800 text-slate-200 hover:bg-slate-800'
                    }`}
                  >
                    <span className={`font-extrabold shrink-0 ${isSelected ? 'text-white' : 'text-blue-400'}`}>
                      {letter})
                    </span>
                    <InteractiveText 
                      text={cleanText} 
                      onWordClick={handleWordClick} 
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* FEEDBACK AMABLE Y PEDAGÓGICO DE RESPUESTA */}
          {feedback && (
            <div className={`p-4 rounded-2xl text-xs font-bold text-left border animate-fadeIn leading-relaxed whitespace-pre-line ${
              feedback.type === 'success' ? 'bg-emerald-950/90 border-emerald-500/60 text-emerald-200' : 'bg-amber-950/90 border-amber-500/60 text-amber-200'
            }`}>
              {feedback.text}
            </div>
          )}

          {/* BOTÓN CONTINUAR O VER RESULTADOS FINALES */}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleAnswerSubmit}
              disabled={!selectedOpt}
              className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-xl text-xs shadow-lg transition cursor-pointer"
            >
              {currentIndex + 1 === questions.length ? 'Comprobar y Ver Calificación Final 🚀' : 'Comprobar Respuesta 🚀'}
            </button>
          </div>

        </div>
      ) : (
        /* RESULTADO FINAL + TABLA DE CLAVE ESTRUCTURADA CON EXPLICACIÓN */
        <div className="bg-slate-900/95 border border-slate-700 p-8 rounded-3xl space-y-6 shadow-2xl animate-fadeIn">
          <div className="text-center space-y-2">
            <div className="text-4xl">🏆</div>
            <h3 className="text-2xl font-extrabold text-white">¡Misión Completada!</h3>
            <p className="text-xs text-slate-400">Resultados registrados en tu Pasaporte Digital EOH</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800">
              <div className="text-[10px] font-bold text-slate-400 uppercase">Puntaje Obtenido</div>
              <div className="text-2xl font-black text-blue-400 mt-1">{score} / {questions.length}</div>
            </div>

            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800">
              <div className="text-[10px] font-bold text-slate-400 uppercase">Porcentaje de Aciertos</div>
              <div className="text-2xl font-black text-emerald-400 mt-1">{Math.round((score / questions.length) * 100)}%</div>
            </div>

            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800">
              <div className="text-[10px] font-bold text-slate-400 uppercase">Puntos XP Ganados</div>
              <div className="text-2xl font-black text-amber-400 mt-1">+{score * 10} XP</div>
            </div>
          </div>

          <button
            onClick={handleReShuffleAll}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl transition shadow-lg text-xs cursor-pointer"
          >
            🔄 Intentar Otra Ronda de Ejercicios Únicos
          </button>
        </div>
      )}

      {/* EMERGENTE MODAL DE TRADUCCIÓN AL HACER CLIC EN PALABRAS */}
      {activeWordData && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-slate-900 border border-blue-500/50 rounded-3xl max-w-sm w-full p-6 relative shadow-2xl space-y-4 text-left">
            <button 
              onClick={() => setActiveWordData(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white font-bold text-lg bg-slate-800 w-7 h-7 rounded-full flex items-center justify-center transition cursor-pointer"
            >
              ✕
            </button>

            <div className="border-b border-slate-800 pb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 bg-blue-950 px-2 py-0.5 rounded-full border border-blue-500/30">
                {activeWordData.pos || 'Vocabulario'}
              </span>
              <h4 className="text-xl font-black text-white mt-1 capitalize">
                {activeWordData.word}
              </h4>
            </div>

            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Traducción en Español:</span>
              <p className="text-base font-bold text-emerald-400">{activeWordData.trans}</p>
            </div>

            <button
              onClick={() => setActiveWordData(null)}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-xl transition text-xs shadow-lg cursor-pointer"
            >
              Entendido 👍
            </button>
          </div>
        </div>
      )}

    </div>
  );
}